"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Bell, Check, Search, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api-client";
import { formatRelativeTime } from "@/lib/formatters";
import type { Notification } from "@/types";

const POLL_MS = 30_000;

interface NotificationPayload {
  saved_search_id?: string;
  saved_search_name?: string;
  opportunity_id?: string;
  opportunity_title?: string;
  defendant?: string | null;
  sector?: string | null;
  match_count_total?: number;
  delivered_count?: number;
}


function NotificationIcon({ kind }: { kind: string }) {
  if (kind === "saved_search_truncated")
    return <AlertTriangle className="size-4 text-amber-600" />;
  if (kind === "saved_search_match")
    return <Search className="size-4 text-primary" />;
  return <Bell className="size-4 text-muted-foreground" />;
}

function NotificationRow({
  notification,
  onSeen,
}: {
  notification: Notification;
  onSeen: (id: string) => void;
}) {
  const p = (notification.payload ?? {}) as NotificationPayload;
  const unread = !notification.seen_at;

  const title =
    notification.kind === "saved_search_truncated"
      ? `"${p.saved_search_name ?? "Saved search"}" returned ${p.match_count_total ?? 0}+ matches`
      : p.opportunity_title ?? "New match";

  const subtitle =
    notification.kind === "saved_search_truncated"
      ? "Refine the filter to see more results"
      : [p.defendant, p.sector, p.saved_search_name]
          .filter(Boolean)
          .join(" · ");

  const href =
    notification.kind === "saved_search_match" && p.opportunity_id
      ? `/discovery/${p.opportunity_id}`
      : "/discovery";

  return (
    <Link
      href={href}
      onClick={() => {
        if (unread) onSeen(notification.id);
      }}
      className={cn(
        "flex items-start gap-2 border-b border-border px-3 py-2 transition-colors hover:bg-muted/50 last:border-b-0",
        unread && "bg-accent/5",
      )}
    >
      <div className="mt-0.5">
        <NotificationIcon kind={notification.kind} />
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "truncate text-sm",
            unread ? "font-medium text-foreground" : "text-muted-foreground",
          )}
        >
          {title}
        </p>
        {subtitle && (
          <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
        )}
        <p className="mt-0.5 text-[10px] text-muted-foreground">
          {formatRelativeTime(notification.created_at)}
        </p>
      </div>
      {unread && (
        <span className="mt-1.5 inline-block size-1.5 shrink-0 rounded-full bg-primary" />
      )}
    </Link>
  );
}

export function NotificationDropdown() {
  const [items, setItems] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  // Initial load + poll every 30s while mounted. Runs inside an async
  // IIFE so setState calls are not synchronous children of the effect
  // body (avoids react-hooks/set-state-in-effect).
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const result = await api.listNotifications({ limit: 10 });
        if (cancelled) return;
        setItems(result.data);
        setUnreadCount(result.unread_count);
      } catch {
        // Silent — next poll reconciles
      }
    };
    void fetchOnce();
    const handle = setInterval(() => {
      if (!cancelled) void fetchOnce();
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, []);

  // Refresh when dropdown opens so user sees the latest
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await api.listNotifications({ limit: 10 });
        if (cancelled) return;
        setItems(result.data);
        setUnreadCount(result.unread_count);
      } catch {
        // Silent
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSeen = async (id: string) => {
    // Optimistic update
    setItems((prev) =>
      prev.map((n) =>
        n.id === id ? { ...n, seen_at: new Date().toISOString() } : n,
      ),
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await api.markNotificationSeen(id);
    } catch {
      // Silent — next poll reconciles
    }
  };

  const handleMarkAll = async () => {
    setItems((prev) =>
      prev.map((n) =>
        n.seen_at ? n : { ...n, seen_at: new Date().toISOString() },
      ),
    );
    setUnreadCount(0);
    try {
      await api.markAllNotificationsSeen();
    } catch {
      // Silent
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        render={
          <button
            type="button"
            className="relative inline-flex size-11 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Notifications"
          >
            <Bell className="size-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold leading-none text-accent-foreground">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        }
      />
      <DropdownMenuContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <p className="text-sm font-medium text-foreground">Notifications</p>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleMarkAll}
            >
              <Check className="size-3" />
              Mark all as read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground">
              No notifications yet. Save a search from Discovery to get alerts
              when new matches appear.
            </div>
          ) : (
            items.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onSeen={handleSeen}
              />
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
